import type { ReactNode } from 'react'
import type {
  AdviceCardStat,
  ElitePlansAdviceCardInsightTone
} from '../types/adviceCards.types'
import { ChevronRightIcon } from '../icons/ChevronRightIcon'
import Info from '../../../components/ui-components/Info'
import { BackgroundDots } from '../icons/BackgroundDots'
import {
  DEFAULT_ADVICE_CARD_GRADIENT,
  DEFAULT_ADVICE_DOTS_COLOR
} from '../constants/adviceCardTheme'

export type { ElitePlansAdviceCardInsightTone } from '../types/adviceCards.types'
export { DEFAULT_ADVICE_CARD_GRADIENT, DEFAULT_ADVICE_DOTS_COLOR } from '../constants/adviceCardTheme'

export type ElitePlansAdviceCardProps = {
  title: string
  body: string
  icon: ReactNode
  stats: AdviceCardStat[]
  statusLabel?: string
  insightTone?: ElitePlansAdviceCardInsightTone
  accentColor?: string
  backgroundGradient?: string
  dotsColor?: string
  borderColor?: string
  onClick?: () => void
}

function resolveHighlightColor(
  accentColor: string,
  insightTone: ElitePlansAdviceCardInsightTone
): string {
  if (insightTone === 'success') {
    return 'var(--status-text-in-form, #2A9C4A)'
  }
  return accentColor
}

export function ElitePlansAdviceCard({
  title,
  body,
  icon,
  stats,
  statusLabel,
  insightTone = 'orange',
  accentColor = 'var(--ep-accent)',
  backgroundGradient = DEFAULT_ADVICE_CARD_GRADIENT,
  dotsColor = DEFAULT_ADVICE_DOTS_COLOR,
  borderColor,
  onClick
}: ElitePlansAdviceCardProps) {
  const highlightColor = resolveHighlightColor(accentColor, insightTone)

  const content = (
    <>
      <div className='pointer-events-none absolute inset-0 top-[-100px] overflow-hidden opacity-25' aria-hidden='true'>
        <BackgroundDots color={dotsColor} className='h-full w-full' width={280} height={220} />
      </div>

      <div className='relative z-[1] flex h-full flex-col'>
        <div className='flex flex-row justify-between'> <div className='flex flex-col'><div className='mb-2 flex items-start justify-between gap-2'>
          {icon}
        </div>
          <h3 className='text-sm font-medium text-black'>{title}</h3>
          <p className='mt-1 text-sm leading-[1.4] text-grey40'>{body}</p>
          {statusLabel ? (
            <p className='mt-3 text-sm font-medium leading-[1.4]' style={{ color: highlightColor }}>
              {statusLabel}
            </p>

          ) : null}
        </div>
          <div>
            <ChevronRightIcon size={14} color='#232323' className='mt-5 opacity-20' />
          </div>
        </div>
        {stats.length > 0 ? (
          <div className={`${statusLabel ? 'pt-3' : 'pt-6'}`} style={{ color: highlightColor }}>
            <Info className='!gap-2' lineClassName='!bg-current opacity-95' contentClassName='!text-base'>
              <ul className='list-none space-y-0.5 p-0'>
                {stats.map((stat) => (
                  <li key={`${stat.value ?? ''}-${stat.label}`} className='flex flex-wrap items-baseline gap-1'>
                    {stat.emphasize ? (
                      <span className='font-medium' style={{ color: highlightColor }}>
                        {stat.label}
                      </span>
                    ) : (
                      <>
                        {stat.value ? (
                          <span className='font-medium' style={{ color: highlightColor }}>
                            {stat.value}
                          </span>
                        ) : null}
                        <span className='text-grey50'>{stat.label}</span>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </Info>
          </div>
        ) : null}
      </div>
    </>
  )

  return (
    <li className='min-h-[240px] w-[300px] shrink-0 snap-start'>
      {onClick ? (
        <button
          type='button'
          className='ep-focus-ring relative flex h-full min-h-[240px] w-full flex-col overflow-hidden rounded-[var(--ep-radius)] border-none p-4 text-left shadow-[var(--ep-shadow)]'
          style={{
            background: backgroundGradient,
            border: borderColor ? `1px solid ${borderColor}` : undefined
          }}
          onClick={onClick}
        >
          {content}
        </button>
      ) : (
        <article
          className='relative flex h-full min-h-[240px] flex-col overflow-hidden rounded-[var(--ep-radius)] p-4 shadow-[var(--ep-shadow)]'
          style={{
            background: backgroundGradient,
            border: borderColor ? `1px solid ${borderColor}` : undefined
          }}
        >
          {content}
        </article>
      )}
    </li>
  )
}
